import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Activity, RefreshCw, MessageSquare, AlertCircle, CheckCircle2, Brain, Search, BarChart3, Loader2, Clock, BookOpen, ExternalLink, Wand2, AlertTriangle, Pin, Trash2, Plus, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RANGE_OPTIONS = [
  { days: 7,  label: '7d'  },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

function StatCard({ label, value, sub, accent }) {
  const accentMap = {
    green:  'border-emerald-500/30 bg-emerald-500/[0.04]',
    amber:  'border-amber-500/30 bg-amber-500/[0.04]',
    red:    'border-red-500/30 bg-red-500/[0.04]',
    cyan:   'border-cyan-500/30 bg-cyan-500/[0.04]',
    slate:  'border-border/40 bg-muted/10',
  };
  return (
    <div className={`p-4 rounded-xl border ${accentMap[accent] || accentMap.slate}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold text-foreground mt-1">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground/70 mt-1 leading-tight">{sub}</p>}
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
}

function WordingLab() {
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingKind, setSavingKind] = useState('');

  const load = async () => {
    try {
      const r = await api.getClarifyWording();
      const list = r.items || [];
      setItems(list);
      const d = {};
      list.forEach((it) => { d[it.kind] = { a: it.override_a || '', b: it.override_b || '' }; });
      setDrafts(d);
    } catch (e) { /* admin-only; hide on failure */ setItems([]); }
  };

  useEffect(() => { load(); }, []);

  const save = async (it) => {
    setSavingKind(it.kind);
    try {
      const d = drafts[it.kind] || { a: '', b: '' };
      await api.setClarifyWording({ kind: it.kind, a: d.a, b: d.b });
      toast.success(`Saved wording for “${it.title}”`);
      await load();
    } catch (e) { toast.error(e.message || 'Failed to save wording'); }
    finally { setSavingKind(''); }
  };

  const reset = async (it) => {
    setSavingKind(it.kind);
    try {
      await api.resetClarifyWording(it.kind);
      toast.success(`Reset “${it.title}” to default`);
      await load();
    } catch (e) { toast.error(e.message || 'Failed to reset'); }
    finally { setSavingKind(''); }
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/[0.02] p-5" data-testid="wording-lab-section">
      <div className="flex items-center gap-2 mb-1">
        <Wand2 className="w-4 h-4 text-fuchsia-400" />
        <h2 className="text-sm font-heading font-semibold text-foreground">Wording Lab</h2>
        <span className="ml-auto text-[10px] text-muted-foreground/70 italic">Edit the two A/B phrasings of each clarify prompt · tenant-wide</span>
      </div>
      <p className="text-[11px] text-muted-foreground/70 mb-4">Blank fields fall back to the built-in default. Both variants keep running as an A/B test, so the pick-rate panel above still tunes the winner.</p>
      <div className="space-y-4">
        {items.map((it) => {
          const d = drafts[it.kind] || { a: '', b: '' };
          const dirty = (d.a || '') !== (it.override_a || '') || (d.b || '') !== (it.override_b || '');
          const edited = !!(it.override_a || it.override_b);
          return (
            <div key={it.kind} className="rounded-xl border border-border/30 bg-background/40 p-4" data-testid={`wording-kind-${it.kind}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-foreground">{it.title}</span>
                {edited && <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-300">edited</span>}
                {it.placeholders?.length > 0 && (
                  <span className="ml-auto text-[10px] text-muted-foreground/80">placeholders: {it.placeholders.map((p) => `{${p}}`).join(' ')}</span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['a', 'b'].map((v) => (
                  <div key={v}>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Variant {v.toUpperCase()}</label>
                    <textarea
                      data-testid={`wording-input-${it.kind}-${v}`}
                      rows={2}
                      value={d[v]}
                      placeholder={it[`default_${v}`]}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [it.kind]: { ...prev[it.kind], [v]: e.target.value } }))}
                      className="mt-1 w-full text-xs rounded-lg border border-border/40 bg-background/60 px-2.5 py-2 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-fuchsia-500/40 resize-none"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <Button size="sm" className="h-7 text-xs" disabled={!dirty || savingKind === it.kind} onClick={() => save(it)} data-testid={`wording-save-${it.kind}`}>
                  {savingKind === it.kind ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={!edited || savingKind === it.kind} onClick={() => reset(it)} data-testid={`wording-reset-${it.kind}`}>
                  Reset to default
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_CUSTOM = { id: null, title: '', triggers: '', question_a: '', question_b: '', narrative: '', options: [{ label: '', patch: '' }, { label: '', patch: '' }], enabled: true };

function MiniSparkline({ trend }) {
  if (!trend || trend.length < 2) return null;
  const total = trend.reduce((s, t) => s + (t.fired || 0) + (t.picked || 0), 0);
  if (total === 0) return null;
  const w = 60, h = 16, n = trend.length;
  const max = Math.max(1, ...trend.map(t => Math.max(t.fired || 0, t.picked || 0)));
  const pts = (key) => trend.map((t, i) => `${((i / (n - 1)) * w).toFixed(1)},${(h - ((t[key] || 0) / max) * (h - 2) - 1).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0" data-testid="custom-clarify-sparkline" aria-label="fired vs picked, last 14 days">
      <polyline points={pts('fired')} fill="none" stroke="currentColor" strokeWidth="1" className="text-muted-foreground/70" />
      <polyline points={pts('picked')} fill="none" stroke="currentColor" strokeWidth="1.25" className="text-teal-400" />
    </svg>
  );
}

function CustomClarifyManager({ seederRef }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null); // null = closed
  const [saving, setSaving] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [refineText, setRefineText] = useState('');
  const rootRef = React.useRef(null);

  const load = async () => {
    try { const r = await api.listCustomClarify(); setItems(r.items || []); }
    catch (e) { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const openNew = async (seed, modelId) => {
    let triggers = seed || '';
    let options = [{ label: '', patch: '' }, { label: '', patch: '' }];
    if (seed) {
      try {
        const s = await api.suggestCustomClarify(seed, modelId || '');
        if (s.triggers?.length) triggers = s.triggers.join(', ');
        if ((s.options || []).length >= 2) options = s.options.map(o => ({ label: o.label || '', patch: o.patch || '' }));
      } catch (e) { /* keep the raw seed as a fallback */ }
    }
    setForm({ ...EMPTY_CUSTOM, options, title: seed ? seed.slice(0, 48) : '', triggers, _seedQuestion: seed || '', _seedModel: modelId || '' });
    if (rootRef.current) rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openEdit = (it) => setForm({
    id: it.id, title: it.title || '', triggers: (it.triggers || []).join(', '),
    question_a: it.question_a || '', question_b: it.question_b || '', narrative: it.narrative || '',
    options: (it.options && it.options.length ? it.options : [{ label: '', patch: '' }, { label: '', patch: '' }]).map(o => ({ label: o.label || '', patch: o.patch || '' })),
    enabled: it.enabled !== false,
  });

  // let the parent (coverage-gap rows) open a prefilled "new prompt" form
  useEffect(() => { if (seederRef) seederRef.current = openNew; }, [seederRef]);

  const setOpt = (i, key, val) => setForm(f => ({ ...f, options: f.options.map((o, j) => j === i ? { ...o, [key]: val } : o) }));
  const addOpt = () => setForm(f => ({ ...f, options: [...f.options, { label: '', patch: '' }] }));
  const removeOpt = (i) => setForm(f => ({ ...f, options: f.options.filter((_, j) => j !== i) }));

  const aiDraft = async (instruction) => {
    const q = (form._seedQuestion || form.title || '').trim();
    if (!q) { toast.error('Add a question or title first'); return; }
    setAiDrafting(true);
    try {
      const payload = { question: q, model_id: form._seedModel || '' };
      if (instruction) {
        payload.instruction = instruction;
        payload.current = {
          title: form.title, question_a: form.question_a, question_b: form.question_b,
          triggers: form.triggers.split(/[,\n]/).map(t => t.trim()).filter(Boolean),
          options: form.options.filter(o => o.label || o.patch),
        };
      }
      const d = await api.aiDraftCustomClarify(payload);
      setForm(f => ({
        ...f,
        title: d.title || f.title,
        triggers: (d.triggers && d.triggers.length) ? d.triggers.join(', ') : f.triggers,
        question_a: d.question_a || f.question_a,
        question_b: d.question_b || f.question_b,
        options: (d.options && d.options.length >= 2) ? d.options.map(o => ({ label: o.label || '', patch: o.patch || '' })) : f.options,
      }));
      toast.success(instruction ? 'Refined the draft — review & save' : 'AI drafted a prompt — review & save');
      if (instruction) setRefineText('');
    } catch (e) { toast.error(e.message || 'AI draft failed'); }
    finally { setAiDrafting(false); }
  };

  const save = async () => {
    const payload = {
      title: form.title.trim(),
      triggers: form.triggers.split(/[,\n]/).map(t => t.trim()).filter(Boolean),
      question_a: form.question_a.trim(), question_b: form.question_b.trim(),
      narrative: form.narrative.trim(),
      options: form.options.map(o => ({ label: o.label.trim(), patch: (o.patch.trim() || o.label.trim()) })).filter(o => o.label || o.patch),
      enabled: form.enabled,
    };
    setSaving(true);
    try {
      if (form.id) await api.updateCustomClarify(form.id, payload);
      else await api.createCustomClarify(payload);
      toast.success(form.id ? 'Prompt updated' : 'Custom prompt created');
      setForm(null); await load();
    } catch (e) { toast.error(e.message || 'Failed to save prompt'); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async (it) => {
    try {
      await api.updateCustomClarify(it.id, { ...it, enabled: it.enabled === false });
      await load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };
  const del = async (it) => {
    try { await api.deleteCustomClarify(it.id); toast.success('Prompt deleted'); await load(); }
    catch (e) { toast.error(e.message || 'Failed to delete'); }
  };

  if (!items) return null;

  return (
    <div ref={rootRef} className="rounded-2xl border border-teal-500/20 bg-teal-500/[0.02] p-5" data-testid="custom-clarify-section">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-4 h-4 text-teal-400" />
        <h2 className="text-sm font-heading font-semibold text-foreground">Custom clarify prompts</h2>
        <span className="ml-auto" />
        <Button size="sm" className="h-7 text-xs" onClick={() => openNew('')} data-testid="custom-clarify-new">+ New prompt</Button>
      </div>
      <p className="text-[11px] text-muted-foreground/70 mb-4">Author your own guided prompt for a recurring question. It fires when <span className="font-semibold">all</span> trigger words appear and the question isn't already specific. Each option rewrites the question so it won't loop.</p>

      {items.length === 0 && !form && (
        <p className="text-xs text-muted-foreground">No custom prompts yet — create one, or use “Make prompt” on a coverage gap below.</p>
      )}

      {items.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg border border-border/20 bg-background/40" data-testid={`custom-clarify-row-${it.id}`}>
              <span className="font-semibold text-foreground truncate max-w-[30%]" title={it.title}>{it.title || '(untitled)'}</span>
              <span className="text-muted-foreground/70 truncate flex-1" title={(it.triggers || []).join(', ')}>triggers: {(it.triggers || []).join(', ') || '—'}</span>
              <span className="text-[9px] text-muted-foreground/80 flex-shrink-0">{(it.options || []).length} opts</span>
              <span className="text-[9px] text-muted-foreground/70 flex-shrink-0" title="times shown · times an option was picked" data-testid={`custom-clarify-usage-${it.id}`}>fired {it.fired || 0} · picked {it.picked || 0}</span>
              <MiniSparkline trend={it.trend} />
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${it.enabled !== false ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300' : 'border-border/40 text-muted-foreground'}`}>{it.enabled !== false ? 'on' : 'off'}</span>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => openEdit(it)} data-testid={`custom-clarify-edit-${it.id}`}>Edit</Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => toggleEnabled(it)} data-testid={`custom-clarify-toggle-${it.id}`}>{it.enabled !== false ? 'Disable' : 'Enable'}</Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-red-400 hover:text-red-300" onClick={() => del(it)} data-testid={`custom-clarify-delete-${it.id}`}>Delete</Button>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="rounded-xl border border-teal-500/30 bg-background/50 p-4 space-y-3" data-testid="custom-clarify-form">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground/70">{form.id ? 'Edit prompt' : 'New prompt'}</span>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10" disabled={aiDrafting || !(form._seedQuestion || form.title)} onClick={() => aiDraft()} data-testid="custom-clarify-ai-draft">
                {aiDrafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Draft with AI
              </Button>
            </div>
            {(form.question_a || form.options.some(o => o.label || o.patch)) && (
              <div className="flex items-center gap-2">
                <input value={refineText} onChange={e => setRefineText(e.target.value)} placeholder={'Refine with AI — e.g. "make it shorter" or "add a Net Premium option"'} className="flex-1 text-[11px] rounded-lg border border-fuchsia-500/20 bg-background/60 px-2.5 py-1.5 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-fuchsia-500/40" data-testid="custom-clarify-refine-input" />
                <Button size="sm" variant="ghost" className="h-7 text-xs text-fuchsia-300" disabled={aiDrafting || !refineText.trim()} onClick={() => aiDraft(refineText.trim())} data-testid="custom-clarify-refine-btn">Refine</Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Title</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Premium type" className="mt-1 w-full text-xs rounded-lg border border-border/40 bg-background/60 px-2.5 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-teal-500/40" data-testid="custom-clarify-title" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Trigger words (all required, comma-separated)</label>
              <input value={form.triggers} onChange={e => setForm(f => ({ ...f, triggers: e.target.value }))} placeholder="premium, growth" className="mt-1 w-full text-xs rounded-lg border border-border/40 bg-background/60 px-2.5 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-teal-500/40" data-testid="custom-clarify-triggers" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['question_a', 'question_b'].map((k, idx) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Question — Variant {idx === 0 ? 'A' : 'B (optional)'}</label>
                <textarea rows={2} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={idx === 0 ? 'Which premium did you mean?' : 'Falls back to Variant A if blank'} className="mt-1 w-full text-xs rounded-lg border border-border/40 bg-background/60 px-2.5 py-2 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-teal-500/40 resize-none" data-testid={`custom-clarify-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Answer options (≥2 — each rewrites the question)</label>
            <div className="space-y-2 mt-1">
              {form.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`custom-clarify-option-${i}`}>
                  <input value={o.label} onChange={e => setOpt(i, 'label', e.target.value)} placeholder="Label (e.g. Written)" className="flex-1 text-xs rounded-lg border border-border/40 bg-background/60 px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-teal-500/40" data-testid={`custom-clarify-option-label-${i}`} />
                  <input value={o.patch} onChange={e => setOpt(i, 'patch', e.target.value)} placeholder="Appended to question (e.g. written premium)" className="flex-1 text-xs rounded-lg border border-border/40 bg-background/60 px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-teal-500/40" data-testid={`custom-clarify-option-patch-${i}`} />
                  {form.options.length > 2 && (
                    <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2 text-red-400" onClick={() => removeOpt(i)} data-testid={`custom-clarify-option-remove-${i}`}>✕</Button>
                  )}
                </div>
              ))}
            </div>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] mt-1.5" onClick={addOpt} data-testid="custom-clarify-add-option">+ Add option</Button>
          </div>
          <div className="rounded-lg border border-border/30 bg-muted/10 p-3" data-testid="custom-clarify-preview">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mb-2">Preview — how users will see it</p>
            <p className="text-xs text-foreground mb-2">{form.question_a || form.question_b || 'Your prompt question…'}</p>
            <div className="flex flex-wrap gap-1.5">
              {form.options.filter(o => o.label || o.patch).map((o, i) => (
                <span key={i} className="px-2 py-1 rounded-lg border border-primary/30 bg-primary/10 text-[11px] text-foreground">{o.label || o.patch}</span>
              ))}
            </div>
            {form.question_b && form.question_b !== form.question_a && (
              <p className="text-[10px] text-muted-foreground/80 mt-2">Variant B: “{form.question_b}”</p>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs" disabled={saving} onClick={save} data-testid="custom-clarify-save">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : (form.id ? 'Update' : 'Create')}</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setForm(null)} data-testid="custom-clarify-cancel">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RankingDefaults({ models }) {
  const [modelId, setModelId] = useState('');
  const [pins, setPins] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [subject, setSubject] = useState('');
  const [measureName, setMeasureName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (models.length && !modelId) setModelId(models[0].model_id || models[0].id);
  }, [models]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (mid) => {
    if (!mid) return;
    try { const r = await api.listRankingDefaults(mid); setPins(r.items || []); } catch { setPins([]); }
    try {
      const full = await api.getSemanticModelFull(mid);
      const seen = {}; const ms = [];
      (full.entities || []).forEach(e => (e.attributes || []).forEach(a => {
        if ((a.semantic_type || '').toLowerCase() === 'measure' && a.name && !seen[a.name.toLowerCase()]) {
          seen[a.name.toLowerCase()] = 1;
          ms.push({ name: a.name, label: a.business_name || a.display_name || a.name, certified: (a.certification?.status) === 'certified' });
        }
      }));
      try {
        const calc = await api.listModelMeasures(mid);
        (calc || []).forEach(m => {
          if (m.name && !seen[m.name.toLowerCase()]) {
            seen[m.name.toLowerCase()] = 1;
            ms.push({ name: m.name, label: m.business_name || m.name, certified: (m.certification?.status) === 'certified' });
          }
        });
      } catch { /* calc measures optional */ }
      ms.sort((a, b) => (Number(b.certified) - Number(a.certified)) || a.label.localeCompare(b.label));
      setMeasures(ms);
    } catch { setMeasures([]); }
  };
  useEffect(() => { load(modelId); }, [modelId]);  

  const save = async () => {
    const subj = subject.trim().toLowerCase();
    if (!subj || !measureName) { toast.error('Enter a subject and pick a measure'); return; }
    const m = measures.find(x => x.name === measureName);
    setSaving(true);
    try {
      await api.setRankingDefault({ model_id: modelId, subject: subj, measure_name: measureName, measure_label: m?.label || measureName, certified: !!m?.certified });
      toast.success(`Pinned "${subj}" → ${m?.label || measureName}`);
      setSubject(''); setMeasureName('');
      await load(modelId);
    } catch { toast.error('Could not save default'); }
    setSaving(false);
  };
  const remove = async (p) => {
    try { await api.deleteRankingDefault({ model_id: modelId, subject: p.subject }); toast.success('Removed'); await load(modelId); }
    catch { toast.error('Could not remove'); }
  };

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.02] p-5" data-testid="ranking-defaults-section">
      <div className="flex items-center gap-2 mb-1">
        <Pin className="w-4 h-4 text-amber-500" />
        <h2 className="text-sm font-heading font-semibold text-foreground">Ranking defaults</h2>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">
        Pin the default measure AI Explore ranks by for a subject (e.g. <b>claims → Incurred</b>). Used automatically when a question doesn't name a measure and the user has no saved choice.
      </p>
      <div className="flex items-center gap-2 mb-3">
        <select value={modelId} onChange={(e) => setModelId(e.target.value)}
          className="h-8 text-xs bg-background border border-border/50 rounded-lg px-2 text-foreground" data-testid="ranking-defaults-model-select">
          {models.map(m => { const id = m.model_id || m.id; return <option key={id} value={id}>{m.name || m.display_name || id}</option>; })}
        </select>
      </div>
      <div className="space-y-1.5 mb-4" data-testid="ranking-defaults-list">
        {pins.length === 0 && <p className="text-[11px] text-muted-foreground italic">No defaults pinned for this model yet.</p>}
        {pins.map(p => (
          <div key={p.subject} className="flex items-center gap-2 text-xs rounded-lg border border-border/40 bg-background/40 px-3 py-1.5" data-testid={`ranking-default-${p.subject}`}>
            <span className="font-medium text-foreground">{p.subject}</span>
            <ArrowRightMini />
            <span className="text-foreground">{p.measure_label || p.measure_name}</span>
            {p.certified
              ? <span className="inline-flex items-center gap-1 text-[9px] text-emerald-800 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5"><ShieldCheck className="w-2.5 h-2.5" /> certified</span>
              : <span className="text-[9px] text-amber-800 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-1.5 py-0.5">ad-hoc</span>}
            <button onClick={() => remove(p)} className="ml-auto p-1 rounded hover:bg-muted text-muted-foreground" title="Remove" data-testid={`ranking-default-remove-${p.subject}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. claims)"
          className="h-8 text-xs bg-background border border-border/50 rounded-lg px-2 text-foreground w-40" data-testid="ranking-default-subject-input" />
        <select value={measureName} onChange={(e) => setMeasureName(e.target.value)}
          className="h-8 text-xs bg-background border border-border/50 rounded-lg px-2 text-foreground" data-testid="ranking-default-measure-select">
          <option value="">Choose a measure…</option>
          {measures.map(m => <option key={m.name} value={m.name}>{m.label}{m.certified ? ' (certified)' : ''}</option>)}
        </select>
        <Button size="sm" onClick={save} disabled={saving} className="h-8" data-testid="ranking-default-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Pin default</>}
        </Button>
      </div>
    </div>
  );
}

function ArrowRightMini() {
  return <span className="text-muted-foreground">→</span>;
}


export default function ExploreEvalPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [externalSources, setExternalSources] = useState({ events_with_citations: 0, sources: [] });
  const [loading, setLoading] = useState(false);
  // Phase F/G — model coverage audit + refusal log
  const [models, setModels] = useState([]);
  const [covModel, setCovModel] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [refusals, setRefusals] = useState([]);
  const [covLoading, setCovLoading] = useState(false);
  const [synLoading, setSynLoading] = useState(false);
  const [promptStats, setPromptStats] = useState(null);
  const [coverageGaps, setCoverageGaps] = useState(null);
  const customSeederRef = React.useRef(null);

  const autofillSyns = async () => {
    if (!covModel || synLoading) return;
    setSynLoading(true);
    try {
      const r = await api.autofillSynonyms(covModel, { apply: true, limit: 40 });
      toast.success(`Added synonyms to ${r.applied} field(s)${r.remaining ? ` · ${r.remaining} still uncovered — run again` : ' · all covered!'}`);
      await loadCoverage(covModel);
    } catch (e) {
      toast.error(e.message || 'Synonym autofill failed');
    } finally {
      setSynLoading(false);
    }
  };

  const loadCoverage = async (modelId) => {
    if (!modelId) return;
    setCovLoading(true);
    try {
      const [cov, ref] = await Promise.all([
        api.getModelCoverage(modelId),
        api.getExploreRefusals(modelId, 50),
      ]);
      setCoverage(cov);
      setRefusals(ref.refusals || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load coverage');
    } finally {
      setCovLoading(false);
    }
  };

  useEffect(() => {
    api.getSemanticModels().then(list => {
      const arr = Array.isArray(list) ? list : (list?.models || list?.items || []);
      setModels(arr);
      if (arr.length && !covModel) {
        const first = arr[0].model_id || arr[0].id;
        setCovModel(first);
        loadCoverage(first);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (window = days) => {
    setLoading(true);
    try {
      const [s, r, ext] = await Promise.all([
        api.exploreEvalSummary(window),
        api.exploreEvalRecent(50),
        api.exploreEvalExternalSources(window, 20),
      ]);
      setSummary(s);
      setRecent(r.items || []);
      setExternalSources(ext || { events_with_citations: 0, sources: [] });
      try { setPromptStats(await api.clarifyAnalytics(window)); } catch (e) { /* optional */ }
      try { setCoverageGaps(await api.clarifyCoverageGaps(window)); } catch (e) { /* optional */ }
    } catch (e) {
      toast.error(e.message || 'Failed to load eval data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days);   }, [days]);

  const t = summary?.totals || {};
  const modelName = (id) => {
    const mm = models.find(x => (x.model_id || x.id) === id);
    return mm?.name || mm?.display_name || id;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="explore-eval-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Explore Quality
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            What the AI couldn't answer, where it was uncertain, and which semantic models need work.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.days}
              onClick={() => setDays(opt.days)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${days === opt.days ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
              data-testid={`eval-range-${opt.days}`}
            >
              {opt.label}
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => load()} disabled={loading} className="h-8" data-testid="eval-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="eval-stats">
        <StatCard label="Total questions" value={t.total ?? 0} sub={`last ${summary?.window_days || days} days`} accent="slate" />
        <StatCard label="Grounded" value={`${t.grounded_rate ?? 0}%`} sub={`${t.grounded ?? 0} narratives backed by data`} accent="cyan" />
        <StatCard label="Clarify rate" value={`${t.clarify_rate ?? 0}%`} sub={`${t.clarify ?? 0} model gaps surfaced`} accent="amber" />
        <StatCard label="Low confidence" value={`${t.low_confidence_rate ?? 0}%`} sub={`${t.low_confidence ?? 0} weak answers`} accent="red" />
        <StatCard label="Retried" value={t.retried_validation ?? 0} sub="auto-corrections by validate loop" accent="slate" />
      </div>

      {/* Phase G/F — Model coverage audit + refusal log */}
      <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.02] p-5" data-testid="model-coverage-section">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Search className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-heading font-semibold text-foreground">Model coverage &amp; answer-ability</h2>
          <span className="text-[10px] text-muted-foreground/70 italic">Fields the AI can't match hurt answer quality</span>
          <div className="ml-auto flex items-center gap-2">
            <select
              value={covModel}
              onChange={(e) => { setCovModel(e.target.value); loadCoverage(e.target.value); }}
              className="h-8 text-xs bg-background border border-border/50 rounded-lg px-2 text-foreground"
              data-testid="coverage-model-select"
            >
              {models.map(m => {
                const id = m.model_id || m.id;
                return <option key={id} value={id}>{m.name || m.display_name || id}</option>;
              })}
            </select>
            <Button variant="ghost" size="sm" onClick={() => loadCoverage(covModel)} disabled={covLoading} className="h-8" data-testid="coverage-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${covLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {coverage && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="coverage-stats">
              <StatCard label="Coverage score" value={`${coverage.coverage_score}%`} sub="fields with a business name" accent={coverage.coverage_score >= 90 ? 'green' : 'amber'} />
              <StatCard label="Entities" value={coverage.entities} sub={`${coverage.total_fields} fields`} accent="slate" />
              <StatCard label="Relationships" value={coverage.relationships} sub="join graph edges" accent="cyan" />
              <StatCard label="Missing synonyms" value={coverage.counts?.missing_synonyms ?? 0} sub="add aliases to improve matching" accent="amber" />
              <StatCard label="Orphan dims" value={coverage.counts?.orphan_dimensions ?? 0} sub="no relationship — not join-reachable" accent="red" />
            </div>
            {/* Coverage nudges — one-click fixes to close answer-ability gaps */}
            {((coverage.counts?.missing_synonyms ?? 0) > 0 || (coverage.counts?.orphan_dimensions ?? 0) > 0) && (
              <div className="mb-4 space-y-2" data-testid="coverage-nudges">
                {(coverage.counts?.missing_synonyms ?? 0) > 0 && (
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06]" data-testid="nudge-synonyms">
                    <Wand2 className="w-4 h-4 text-amber-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-foreground">{coverage.counts.missing_synonyms} fields have no synonyms</p>
                      <p className="text-[10px] text-muted-foreground/70">Synonyms help the AI match everyday wording to your fields — fewer "can't answer" replies.</p>
                    </div>
                    <Button size="sm" onClick={autofillSyns} disabled={synLoading} className="h-8 shrink-0 bg-amber-500 hover:bg-amber-600 text-white" data-testid="nudge-autofill-synonyms">
                      {synLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      <span className="ml-1.5">{synLoading ? 'Generating…' : 'Auto-fill synonyms (AI)'}</span>
                    </Button>
                  </div>
                )}
                {(coverage.counts?.orphan_dimensions ?? 0) > 0 && (
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-red-500/30 bg-red-500/[0.06]" data-testid="nudge-orphans">
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-foreground">{coverage.counts.orphan_dimensions} dimensions aren't join-reachable</p>
                      <p className="text-[10px] text-muted-foreground/70">Add a relationship in the semantic model so these can be used in answers.</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => navigate('/semantic-models')} className="h-8 shrink-0" data-testid="nudge-open-model">
                      <span>Open model →</span>
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border/30 bg-background/40 p-3">
                <p className="text-[11px] font-semibold text-foreground mb-2">Orphan dimensions ({coverage.counts?.orphan_dimensions ?? 0})</p>
                {(coverage.orphan_dimensions || []).length === 0
                  ? <p className="text-[10px] text-muted-foreground/80 py-3 text-center">All dimensions are join-reachable.</p>
                  : <div className="space-y-1 max-h-40 overflow-auto">
                      {coverage.orphan_dimensions.slice(0, 30).map((o, i) => (
                        <div key={i} className="text-[10px] text-muted-foreground flex gap-2" data-testid={`orphan-dim-${i}`}>
                          <span className="text-muted-foreground/70">{o.entity}</span>·<span className="text-foreground">{o.field}</span>
                        </div>
                      ))}
                    </div>}
              </div>
              <div className="rounded-xl border border-border/30 bg-background/40 p-3" data-testid="refusals-panel">
                <p className="text-[11px] font-semibold text-foreground mb-2">Recent "can't answer" ({refusals.length})</p>
                {refusals.length === 0
                  ? <p className="text-[10px] text-muted-foreground/80 py-3 text-center">No refusals logged for this model yet.</p>
                  : <div className="space-y-1 max-h-40 overflow-auto">
                      {refusals.map((r, i) => (
                        <div key={i} className="p-2 rounded-lg border border-border/20" data-testid={`refusal-${i}`}>
                          <p className="text-[11px] text-foreground leading-snug">{r.question}</p>
                          <p className="text-[9px] text-muted-foreground/80 mt-0.5">matched {r.matched_count ?? 0} field(s) · {fmtTs(r.created_at)}</p>
                        </div>
                      ))}
                    </div>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Two-up: Clarify hits + Low-confidence top */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top clarify */}
        {/* Just-in-time prompt analytics — which prompts fire + which option users pick */}
        <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/[0.02] p-5" data-testid="eval-prompt-analytics-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-indigo-400" />
            <h2 className="text-sm font-heading font-semibold text-foreground">Just-in-time prompts</h2>
            <span className="ml-auto text-[10px] text-muted-foreground/70 italic">Fires &amp; picks — last {promptStats?.days || days}d</span>
          </div>
          {(!promptStats || promptStats.total_shown === 0) ? (
            <p className="text-xs text-muted-foreground">No clarify prompts have fired in this window yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">Prompts shown ({promptStats.total_shown})</p>
                <div className="space-y-1">
                  {promptStats.shown.map((s, i) => {
                    const pct = promptStats.total_shown ? Math.round((s.count / promptStats.total_shown) * 100) : 0;
                    return (
                      <div key={i} className="flex items-center gap-2" data-testid={`prompt-shown-${s.kind}`}>
                        <span className="text-xs text-foreground capitalize w-28 flex-shrink-0">{s.kind.replace(/_/g, ' ')}</span>
                        <div className="flex-1 h-2 rounded-full bg-muted/30 overflow-hidden"><div className="h-full bg-indigo-500/60" style={{ width: `${pct}%` }} /></div>
                        <span className="text-[10px] text-muted-foreground w-14 text-right">{s.count} · {pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">Options picked ({promptStats.total_picked})</p>
                {promptStats.picks.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">No option picks recorded yet.</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {promptStats.picks.slice(0, 12).map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`prompt-pick-${i}`}>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-indigo-500/30 text-indigo-300 capitalize flex-shrink-0">{p.kind.replace(/_/g, ' ')}</span>
                        <span className="text-foreground/80 truncate flex-1">{p.label || '—'}</span>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">{p.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {promptStats && Array.isArray(promptStats.variants) && promptStats.variants.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border/20">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">A/B wording — pick rate by variant</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                {promptStats.variants.map((v, i) => {
                  const won = (promptStats.policy || {})[v.kind] === v.variant;
                  return (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`prompt-variant-${v.kind}-${v.variant}`}>
                    <span className="text-foreground capitalize w-24 flex-shrink-0">{v.kind.replace(/_/g, ' ')}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${won ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300' : 'border-border/40 text-muted-foreground'}`}>{v.variant}{won ? ' ✓' : ''}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted/30 overflow-hidden"><div className="h-full bg-emerald-500/60" style={{ width: `${Math.round((v.pick_rate || 0) * 100)}%` }} /></div>
                    <span className="text-[10px] text-muted-foreground w-24 text-right">{Math.round((v.pick_rate || 0) * 100)}% ({v.picked}/{v.shown})</span>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>


        {/* Prompt coverage report — ran with low confidence + no just-in-time prompt (detector gaps) */}
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.02] p-5" data-testid="eval-coverage-gaps-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-heading font-semibold text-foreground">Prompt coverage gaps</h2>
            <span className="ml-auto text-[10px] text-muted-foreground/70 italic">Low-confidence answers with no prompt{coverageGaps ? ` · ${coverageGaps.total} total` : ''}</span>
          </div>
          {(!coverageGaps || (coverageGaps.items || []).length === 0) ? (
            <p className="text-xs text-muted-foreground">No low-confidence, un-prompted questions in this window — coverage looks good.</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-auto">
              {coverageGaps.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-border/10" data-testid={`coverage-gap-${i}`}>
                  <span className="text-foreground/90 flex-1 truncate" title={it.question}>{it.question || '—'}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-amber-500/30 text-amber-300 capitalize flex-shrink-0">{it.confidence || 'low'}</span>
                  <button
                    onClick={() => customSeederRef.current && customSeederRef.current(it.question || '', it.model_id || '')}
                    className="text-[9px] px-1.5 py-0.5 rounded-md border border-teal-500/40 text-teal-300 hover:bg-teal-500/10 flex-shrink-0 transition-colors"
                    data-testid={`coverage-gap-make-prompt-${i}`}
                  >+ Make prompt</button>
                  <span className="text-[10px] text-muted-foreground/80 flex-shrink-0">{(it.created_at || '').slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>


        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.02] p-5" data-testid="eval-clarify-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-heading font-semibold text-foreground">Coverage gaps (clarify hits)</h2>
            <span className="ml-auto text-[10px] text-muted-foreground/70 italic">Add measures/dims to close these</span>
          </div>
          {summary?.top_clarify_questions?.length === 0 && (
            <p className="text-xs text-muted-foreground/80 text-center py-6">No clarify responses in this window — every question landed on real data.</p>
          )}
          <div className="space-y-1">
            {summary?.top_clarify_questions?.slice(0, 10).map((q, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-border/20 bg-background/40 hover:border-amber-500/30 transition-colors" data-testid={`clarify-q-${i}`}>
                <Badge className="text-[9px] bg-amber-500/15 text-amber-300 border-amber-500/30 flex-shrink-0">×{q.count}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground leading-snug">{q.question}</p>
                  <p className="text-[10px] text-muted-foreground/80 mt-0.5">last asked {fmtTs(q.last_seen)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Low confidence */}
        <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.02] p-5" data-testid="eval-lowconf-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <h2 className="text-sm font-heading font-semibold text-foreground">Low-confidence answers</h2>
            <span className="ml-auto text-[10px] text-muted-foreground/70 italic">Likely metric ambiguity</span>
          </div>
          {summary?.top_low_confidence_questions?.length === 0 && (
            <p className="text-xs text-muted-foreground/80 text-center py-6">No low-confidence answers in this window — AI was confident across the board.</p>
          )}
          <div className="space-y-1">
            {summary?.top_low_confidence_questions?.slice(0, 10).map((q, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-border/20 bg-background/40 hover:border-red-500/30 transition-colors" data-testid={`lowconf-q-${i}`}>
                <Badge className="text-[9px] bg-red-500/15 text-red-300 border-red-500/30 flex-shrink-0">{q.avg_confidence}/100</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground leading-snug">{q.question}</p>
                  <p className="text-[10px] text-muted-foreground/80 mt-0.5">×{q.count} · last asked {fmtTs(q.last_seen)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Wording Lab — edit A/B phrasings of each just-in-time clarify prompt */}
      <WordingLab />

      {/* Custom clarify prompts — admin-authored, incl. from coverage gaps */}
      <CustomClarifyManager seederRef={customSeederRef} />

      {/* Ranking defaults — pin the default ranking measure per subject */}
      <RankingDefaults models={models} />

      {/* Per-model breakdown + retrieval methods */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border/40 bg-background/40 p-5" data-testid="eval-per-model">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-heading font-semibold text-foreground">Per-model quality</h2>
          </div>
          <div className="rounded-lg border border-border/30 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-3 py-2 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              <div>Semantic Model</div>
              <div className="text-right">Total</div>
              <div className="text-right">Clarify</div>
              <div className="text-right">Low conf</div>
              <div className="text-right">Avg conf</div>
            </div>
            {(summary?.per_model || []).map(m => (
              <div key={m.model_id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-3 py-2 border-t border-border/20 items-center text-xs hover:bg-muted/10">
                <div className="text-foreground truncate" title={modelName(m.model_id)}>{modelName(m.model_id)}</div>
                <div className="text-right tabular-nums">{m.total}</div>
                <div className="text-right tabular-nums text-amber-400/80">{m.clarify}</div>
                <div className="text-right tabular-nums text-red-400/80">{m.low_confidence}</div>
                <div className="text-right tabular-nums text-foreground/80">{m.avg_confidence}/100</div>
              </div>
            ))}
            {(summary?.per_model || []).length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground/80">No data yet</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border/40 bg-background/40 p-5" data-testid="eval-retrieval-methods">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-cyan-400" />
            <h2 className="text-sm font-heading font-semibold text-foreground">Retrieval method</h2>
          </div>
          <p className="text-[10px] text-muted-foreground/70 mb-3">How fields were narrowed for the prompt</p>
          <div className="space-y-2">
            {(summary?.retrieval_methods || []).map(m => {
              const total = (summary?.retrieval_methods || []).reduce((s, x) => s + x.count, 0);
              const pct = total ? Math.round((m.count / total) * 100) : 0;
              const labels = { embeddings: 'OpenAI embeddings', llm: 'LLM-as-retriever', lexical: 'Lexical RAG', none: 'None' };
              return (
                <div key={m.method} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground">{labels[m.method] || m.method}</span>
                    <span className="text-muted-foreground tabular-nums">{m.count} · {pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
                    <div className="h-full bg-cyan-500/60" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {(summary?.retrieval_methods || []).length === 0 && (
              <p className="text-xs text-muted-foreground/80 text-center py-2">No data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Most-cited external sources — derived from explore_facts captured
          on each AI Copilot response. Surfaces which third-party orgs the
          AI is leaning on most so compliance teams can audit + the team
          can fast-track real ones into the trusted-URL whitelist. */}
      <div className="rounded-2xl border border-border/40 bg-background/40 p-5" data-testid="eval-external-sources">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-heading font-semibold text-foreground">Most-cited external sources</h2>
          <span className="ml-auto text-[10px] text-muted-foreground/70">
            {externalSources.events_with_citations} event{externalSources.events_with_citations === 1 ? '' : 's'} with citations
          </span>
        </div>
        {(externalSources.sources || []).length === 0 ? (
          <p className="text-xs text-muted-foreground/80 text-center py-6">
            No external citations recorded in this window. The AI either answered everything from tenant data, or hasn&apos;t been asked questions that warrant external context yet.
          </p>
        ) : (
          <div className="space-y-1">
            {externalSources.sources.slice(0, 12).map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2.5 rounded-lg border border-border/20 bg-background/40 hover:border-cyan-500/30 transition-colors items-center" data-testid={`external-source-${i}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {s.sample_url ? (
                      <a href={s.sample_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-cyan-300 hover:underline truncate inline-flex items-center gap-1">
                        {s.source}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <span className="text-xs font-semibold text-foreground truncate" title={s.source}>{s.source}</span>
                    )}
                    {!s.has_verified_url && (
                      <Badge className="text-[8px] bg-amber-500/10 text-amber-300 border-amber-500/20" title="No URL was attached — source is shown as plain text only">
                        no link
                      </Badge>
                    )}
                  </div>
                  {s.sample_claims?.[0] && (
                    <p className="text-[10px] text-muted-foreground/80 leading-snug line-clamp-1" title={s.sample_claims[0]}>
                      &ldquo;{s.sample_claims[0]}&rdquo;
                    </p>
                  )}
                  {s.sample_questions?.[0] && (
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1" title={s.sample_questions.join(' • ')}>
                      asked in: {s.sample_questions[0]}
                      {s.sample_questions.length > 1 && (
                        <span className="ml-1 text-cyan-400/70">+{s.sample_questions.length - 1} more</span>
                      )}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <Badge className="text-[9px] bg-cyan-500/15 text-cyan-300 border-cyan-500/30 tabular-nums">×{s.mention_count}</Badge>
                </div>
                <div className="text-right text-[10px] text-muted-foreground/70 whitespace-nowrap">
                  {fmtTs(s.last_seen)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent stream */}
      <div className="rounded-2xl border border-border/40 bg-background/40 p-5" data-testid="eval-recent">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-heading font-semibold text-foreground">Recent activity</h2>
          <span className="ml-auto text-[10px] text-muted-foreground/70">latest {recent.length}</span>
        </div>
        <div className="rounded-lg border border-border/30 overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 px-3 py-2 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            <div>When</div>
            <div>Question</div>
            <div className="text-right">Type</div>
            <div className="text-right">Conf</div>
            <div className="text-right">Grnd</div>
            <div className="text-right">Method</div>
          </div>
          {recent.slice(0, 25).map((r, i) => (
            <div key={r.event_id || i} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 px-3 py-2 border-t border-border/20 items-center text-xs hover:bg-muted/10" data-testid={`eval-recent-row-${i}`}>
              <div className="text-muted-foreground tabular-nums whitespace-nowrap">{fmtTs(r.created_at)}</div>
              <div className="text-foreground truncate" title={r.question}>{r.question}</div>
              <div className="text-right">
                {r.is_clarify ? (
                  <Badge className="text-[8px] bg-amber-500/15 text-amber-300 border-amber-500/30">clarify</Badge>
                ) : (
                  <Badge variant="outline" className="text-[8px] border-border/40 text-muted-foreground">{r.output_type || '—'}</Badge>
                )}
              </div>
              <div className="text-right tabular-nums">
                {r.low_confidence ? <span className="text-red-400">{r.confidence ?? '—'}</span> : <span className="text-foreground/70">{r.confidence ?? '—'}</span>}
              </div>
              <div className="text-right">{r.grounded ? <CheckCircle2 className="w-3 h-3 text-cyan-400 inline" /> : <span className="text-muted-foreground/70">—</span>}</div>
              <div className="text-right text-[10px] text-muted-foreground">{r.retrieval_method || '—'}</div>
            </div>
          ))}
          {recent.length === 0 && (
            <div className="px-3 py-8 text-center text-xs text-muted-foreground/80">
              <Search className="w-5 h-5 mx-auto mb-2 opacity-40" />
              No Explore activity yet in this window
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
